/**
 * Users Management Page - Complete Edition
 * Advanced user management with modals, sorting, filtering, and export
 */

import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

interface User {
  id: string;
  username: string;
  email: string;
  totalChips: number;
  role: string;
  isBanned: boolean;
  createdAt: string;
  lastLoginAt?: string;
  avatar?: string;
  bannedAt?: string;
  bannedReason?: string;
}

interface UserDetails extends User {
  statistics?: {
    handsPlayed: number;
    handsWon: number;
    winRate: number;
    totalWinnings: number;
  };
}

type SortField = 'username' | 'email' | 'totalChips' | 'role' | 'createdAt' | 'lastLoginAt';
type SortOrder = 'asc' | 'desc';

const UsersPage: React.FC = () => {
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Modals
  const [viewModalUser, setViewModalUser] = useState<UserDetails | null>(null);
  const [editModalUser, setEditModalUser] = useState<User | null>(null);
  const [addUserModal, setAddUserModal] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    role: '',
    email: '',
    chipsOperation: 'add' as 'add' | 'subtract' | 'set',
    chipsAmount: 0,
  });

  // Add user form state
  const [addUserForm, setAddUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'USER',
    totalChips: 10000,
  });

  // Load users on mount and when filters/sort change
  useEffect(() => {
    loadUsers();
  }, [page, search, roleFilter, statusFilter, sortField, sortOrder]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: 50,
        sortBy: sortField,
        sortOrder,
      };

      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter === 'active') params.isBanned = false;
      if (statusFilter === 'banned') params.isBanned = true;

      const data = await adminService.getUsers(params);
      setUsers(data.data);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      alert('Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // View user details
  const handleViewUser = async (user: User) => {
    try {
      const details = await adminService.getUserDetails(user.id);
      setViewModalUser(details);
    } catch (error: any) {
      console.error('Failed to load user details:', error);
      alert('Failed to load user details: ' + error.message);
    }
  };

  // Open edit modal
  const handleOpenEdit = (user: User) => {
    setEditModalUser(user);
    setEditForm({
      role: user.role,
      email: user.email,
      chipsOperation: 'add',
      chipsAmount: 0,
    });
  };

  // Save edited user
  const handleSaveEdit = async () => {
    if (!editModalUser) return;

    try {
      // Update role and email if changed
      if (editForm.role !== editModalUser.role || editForm.email !== editModalUser.email) {
        await adminService.updateUser(editModalUser.id, {
          role: editForm.role,
          email: editForm.email,
        });
      }

      // Update chips if amount specified
      if (editForm.chipsAmount > 0) {
        await adminService.updateUserChips(editModalUser.id, {
          amount: editForm.chipsAmount,
          operation: editForm.chipsOperation,
        });
      }

      setEditModalUser(null);
      loadUsers();
      alert('User updated successfully!');
    } catch (error: any) {
      console.error('Failed to update user:', error);
      alert('Failed to update user: ' + error.message);
    }
  };

  // Ban user
  const handleBanUser = async (userId: string) => {
    const reason = prompt('Enter ban reason:');
    if (!reason) return;

    try {
      await adminService.banUser(userId, { reason });
      loadUsers();
      alert('User banned successfully!');
    } catch (error: any) {
      console.error('Failed to ban user:', error);
      alert('Failed to ban user: ' + error.message);
    }
  };

  // Unban user
  const handleUnbanUser = async (userId: string) => {
    if (!confirm('Are you sure you want to unban this user?')) return;

    try {
      await adminService.unbanUser(userId);
      loadUsers();
      alert('User unbanned successfully!');
    } catch (error: any) {
      console.error('Failed to unban user:', error);
      alert('Failed to unban user: ' + error.message);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId: string, username: string) => {
    if (!confirm(`Are you sure you want to DELETE user "${username}"? This cannot be undone!`)) {
      return;
    }

    try {
      await adminService.deleteUser(userId);
      loadUsers();
      alert('User deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user: ' + error.message);
    }
  };

  // Add new user
  const handleAddUser = async () => {
    try {
      // TODO: Implement create user endpoint
      console.log('Add user:', addUserForm);
      alert('Add user functionality will be implemented when backend endpoint is ready');
      setAddUserModal(false);
      setAddUserForm({
        username: '',
        email: '',
        password: '',
        role: 'USER',
        totalChips: 10000,
      });
    } catch (error: any) {
      console.error('Failed to add user:', error);
      alert('Failed to add user: ' + error.message);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['ID', 'Username', 'Email', 'Chips', 'Role', 'Status', 'Joined', 'Last Login'];
    const rows = users.map((user) => [
      user.id,
      user.username,
      user.email,
      user.totalChips,
      user.role,
      user.isBanned ? 'Banned' : 'Active',
      new Date(user.createdAt).toLocaleDateString(),
      user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never',
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sort indicator
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-800">Users Management</h3>
            <p className="text-gray-600 mt-1">Total: {total} users</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleExportCSV}
              disabled={users.length === 0}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              📥 Export CSV
            </button>
            <button
              onClick={() => setAddUserModal(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              ➕ Add User
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Users
            </label>
            <input
              type="text"
              placeholder="Username or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="banned">Banned Only</option>
              <option value="admins">Admins Only</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Roles</option>
              <option value="USER">User</option>
              <option value="ADMIN">Admin</option>
              <option value="SUPERADMIN">Super Admin</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                setRoleFilter('');
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No users found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      ID
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('username')}
                    >
                      Username <SortIndicator field="username" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('email')}
                    >
                      Email <SortIndicator field="email" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('totalChips')}
                    >
                      Chips <SortIndicator field="totalChips" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('role')}
                    >
                      Role <SortIndicator field="role" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('createdAt')}
                    >
                      Joined <SortIndicator field="createdAt" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('lastLoginAt')}
                    >
                      Last Login <SortIndicator field="lastLoginAt" />
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-mono text-gray-500">
                        {user.id.slice(0, 8)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{user.username}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                      <td className="px-6 py-4 text-sm font-bold text-green-600">
                        {user.totalChips.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            user.role === 'SUPERADMIN'
                              ? 'bg-purple-100 text-purple-800'
                              : user.role === 'ADMIN'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            user.isBanned
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {user.isBanned ? 'Banned' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded transition-colors"
                            title="View Details"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => handleOpenEdit(user)}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                            title="Edit User"
                          >
                            ✏️
                          </button>
                          {user.isBanned ? (
                            <button
                              onClick={() => handleUnbanUser(user.id)}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                              title="Unban User"
                            >
                              ✓
                            </button>
                          ) : (
                            <button
                              onClick={() => handleBanUser(user.id)}
                              className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded transition-colors"
                              title="Ban User"
                            >
                              🚫
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                            title="Delete User"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(page - 1) * 50 + 1} to {Math.min(page * 50, total)} of {total} users
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <div className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Page {page} of {totalPages}
                </div>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* View User Details Modal */}
      {viewModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">User Details</h3>
              <button
                onClick={() => setViewModalUser(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Profile Section */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {viewModalUser.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-800">
                      {viewModalUser.username}
                    </h4>
                    <p className="text-gray-600">{viewModalUser.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">User ID</div>
                    <div className="font-mono text-sm">{viewModalUser.id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Role</div>
                    <div>
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          viewModalUser.role === 'SUPERADMIN'
                            ? 'bg-purple-100 text-purple-800'
                            : viewModalUser.role === 'ADMIN'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {viewModalUser.role}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Chips</div>
                    <div className="font-bold text-green-600">
                      {viewModalUser.totalChips.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Status</div>
                    <div>
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          viewModalUser.isBanned
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {viewModalUser.isBanned ? 'Banned' : 'Active'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Joined</div>
                    <div className="text-sm">
                      {new Date(viewModalUser.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Last Login</div>
                    <div className="text-sm">
                      {viewModalUser.lastLoginAt
                        ? new Date(viewModalUser.lastLoginAt).toLocaleString()
                        : 'Never'}
                    </div>
                  </div>
                </div>

                {/* Ban Info */}
                {viewModalUser.isBanned && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="font-semibold text-red-800 mb-1">Ban Information</div>
                    <div className="text-sm text-red-700">
                      <div>
                        Banned on:{' '}
                        {viewModalUser.bannedAt
                          ? new Date(viewModalUser.bannedAt).toLocaleString()
                          : 'Unknown'}
                      </div>
                      {viewModalUser.bannedReason && (
                        <div>Reason: {viewModalUser.bannedReason}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Statistics Section */}
              {viewModalUser.statistics && (
                <div>
                  <h5 className="font-bold text-gray-800 mb-3">Game Statistics</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-blue-600 font-medium">Hands Played</div>
                      <div className="text-2xl font-bold text-blue-900">
                        {viewModalUser.statistics.handsPlayed}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-green-600 font-medium">Hands Won</div>
                      <div className="text-2xl font-bold text-green-900">
                        {viewModalUser.statistics.handsWon}
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-purple-600 font-medium">Win Rate</div>
                      <div className="text-2xl font-bold text-purple-900">
                        {viewModalUser.statistics.winRate.toFixed(1)}%
                      </div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="text-sm text-orange-600 font-medium">Total Winnings</div>
                      <div className="text-2xl font-bold text-orange-900">
                        {viewModalUser.statistics.totalWinnings.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div>
                <h5 className="font-bold text-gray-800 mb-3">Quick Actions</h5>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setViewModalUser(null);
                      handleOpenEdit(viewModalUser);
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Edit User
                  </button>
                  {viewModalUser.isBanned ? (
                    <button
                      onClick={() => {
                        handleUnbanUser(viewModalUser.id);
                        setViewModalUser(null);
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    >
                      Unban User
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        handleBanUser(viewModalUser.id);
                        setViewModalUser(null);
                      }}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                    >
                      Ban User
                    </button>
                  )}
                  <button
                    onClick={() => {
                      handleDeleteUser(viewModalUser.id, viewModalUser.username);
                      setViewModalUser(null);
                    }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Delete User
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-800">Edit User</h3>
              <button
                onClick={() => setEditModalUser(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Username (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username (read-only)
                </label>
                <input
                  type="text"
                  value={editModalUser.username}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                  <option value="SUPERADMIN">Super Admin</option>
                </select>
              </div>

              {/* Chips Management */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manage Chips
                </label>
                <div className="flex space-x-2 mb-2">
                  <select
                    value={editForm.chipsOperation}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        chipsOperation: e.target.value as 'add' | 'subtract' | 'set',
                      })
                    }
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="add">Add</option>
                    <option value="subtract">Subtract</option>
                    <option value="set">Set To</option>
                  </select>
                  <input
                    type="number"
                    value={editForm.chipsAmount}
                    onChange={(e) =>
                      setEditForm({ ...editForm, chipsAmount: parseInt(e.target.value) || 0 })
                    }
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Amount"
                  />
                </div>
                <div className="text-sm text-gray-600">
                  Current: {editModalUser.totalChips.toLocaleString()} chips
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => setEditModalUser(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {addUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-800">Add New User</h3>
              <button
                onClick={() => setAddUserModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <input
                  type="text"
                  value={addUserForm.username}
                  onChange={(e) =>
                    setAddUserForm({ ...addUserForm, username: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={addUserForm.password}
                  onChange={(e) =>
                    setAddUserForm({ ...addUserForm, password: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={addUserForm.role}
                  onChange={(e) => setAddUserForm({ ...addUserForm, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                  <option value="SUPERADMIN">Super Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Starting Chips
                </label>
                <input
                  type="number"
                  value={addUserForm.totalChips}
                  onChange={(e) =>
                    setAddUserForm({ ...addUserForm, totalChips: parseInt(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => setAddUserModal(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
